import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isPermissionRefused } from '@citibox-panel/api-client';
import { track } from '@citibox-panel/analytics';
import type { AccountTypeClass } from '../lib/accountType';

// The «Desbloquear» WRITE, the block's mirror on a courier account; the mutation variable is the
// agent's reason. `retry: false` here too, although it LIFTS a sanction: a silent retry would
// reactivate an account the agent may believe they failed to reactivate.
//
// On success the event fires first (the closed account class alone, never the reason), then the
// account query is invalidated AND AWAITED: the write returns no account, so the repainted ficha
// is the only acknowledgement, and settling earlier would flash the still-blocked ficha.
export function useUnblockUser(
  userId: string,
  phoneKey: string,
  unblockUser: (id: string, reason: string) => Promise<void>,
  accountClass: AccountTypeClass,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => unblockUser(userId, reason),
    retry: false,
    onSuccess: () => {
      track('user_unblocked', { account_type: accountClass });
      return queryClient.invalidateQueries({ queryKey: ['phone-search', phoneKey] });
    },
    // Fire-and-forget, so the failure reaches the agent without waiting on a read; the refetched
    // ficha resolves who won if another agent got there first.
    onError: (error) => {
      // A permission refusal is the host's to answer; refetching the refused account would only
      // draw a second 403.
      if (isPermissionRefused(error)) return;
      void queryClient.invalidateQueries({ queryKey: ['phone-search', phoneKey] });
    },
  });
}
